import {
  differenceInMilliseconds,
  format,
  formatDistanceToNowStrict,
  formatDuration,
  intervalToDuration,
  isToday,
  isValid,
  isYesterday,
  parseISO,
} from 'date-fns';
import { it } from 'date-fns/locale';
import type { ISODate, ISODateTime } from '@/types';

/** All user-facing dates are Italian; centralised so pages never import locales. */
const locale = { locale: it };

const EMPTY = '—';

function toDate(value: ISODateTime | ISODate | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : parseISO(value);
  return isValid(date) ? date : null;
}

/** `24 ago 2026` */
export function formatDate(value: ISODateTime | ISODate | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'dd MMM yyyy', locale) : EMPTY;
}

/** `24 ago 2026 → 24 ago 2027` or `24 ago 2026 → aperto` when data_fine is null. */
export function contrattoPeriodoLabel(
  dataInizio: ISODate | Date | null | undefined,
  dataFine: ISODate | Date | null | undefined,
): string {
  return `${formatDate(dataInizio)} → ${dataFine ? formatDate(dataFine) : 'aperto'}`;
}

/** `24 ago 2026, 16:05` */
export function formatDateTime(value: ISODateTime | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'dd MMM yyyy, HH:mm', locale) : EMPTY;
}

export interface ReadableDateTime {
  /** `Oggi`, `Ieri`, or `24 ago 2026`. */
  day: string;
  /** `lun` — short weekday, without a trailing dot. */
  weekday: string;
  /** `16:05` */
  time: string;
  /** `martedì 24 agosto 2026, 16:05` */
  long: string;
  /** `2 giorni fa` */
  relative: string;
  iso: string;
}

/**
 * Parts for a session timestamp in a dense table.
 *
 * The calendar day and the clock stay separate so a column of nightly jobs
 * can be scanned by day, and "oggi" / "ieri" replace the date when the
 * session is recent. The long form is the accessible name.
 */
export function readDateTime(value: ISODateTime | Date | null | undefined): ReadableDateTime | null {
  const date = toDate(value);
  if (!date) return null;
  return {
    day: isToday(date) ? 'Oggi' : isYesterday(date) ? 'Ieri' : format(date, 'dd MMM yyyy', locale),
    weekday: format(date, 'EEE', locale).replace(/\.$/, ''),
    time: format(date, 'HH:mm', locale),
    long: format(date, 'EEEE d MMMM yyyy, HH:mm', locale),
    relative: formatDistanceToNowStrict(date, { addSuffix: true, ...locale }),
    iso: date.toISOString(),
  };
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

function localDay(isoDate: string, hours: number, minutes: number, seconds: number, ms: number): string | undefined {
  const match = ISO_DAY.exec(isoDate);
  if (!match) return undefined;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), hours, minutes, seconds, ms);
  return isValid(date) ? date.toISOString() : undefined;
}

/** Inclusive start of a `<input type="date">` value, in the operator's timezone. */
export function startOfLocalDayIso(isoDate: string): string | undefined {
  return localDay(isoDate, 0, 0, 0, 0);
}

/** Inclusive end of a `<input type="date">` value, in the operator's timezone. */
export function endOfLocalDayIso(isoDate: string): string | undefined {
  return localDay(isoDate, 23, 59, 59, 999);
}

/** `16:05:32` – for dense event timelines where the day is already shown. */
export function formatTime(value: ISODateTime | Date | null | undefined): string {
  const date = toDate(value);
  return date ? format(date, 'HH:mm:ss', locale) : EMPTY;
}

/** `2 ore fa` / `in 3 giorni` */
export function formatRelative(value: ISODateTime | Date | null | undefined): string {
  const date = toDate(value);
  return date ? formatDistanceToNowStrict(date, { addSuffix: true, ...locale }) : EMPTY;
}

/** ISO date suitable for `<input type="date">` and query params. */
export function toISODate(value: Date | null | undefined): ISODate | undefined {
  if (!value || !isValid(value)) return undefined;
  return format(value, 'yyyy-MM-dd');
}

/** Human duration from a millisecond span, e.g. `1 h 12 min`. */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return EMPTY;
  if (ms < 1000) return '< 1 s';
  const duration = intervalToDuration({ start: 0, end: Math.abs(ms) });
  const formatted = formatDuration(duration, {
    format: ['days', 'hours', 'minutes'],
    delimiter: ' ',
    ...locale,
  });
  return formatted || '< 1 min';
}

export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return EMPTY;
  return formatDurationMs(seconds * 1000);
}

/** Human duration from a minute count, e.g. `1 ora 30 minuti`. */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes) || minutes < 1) return EMPTY;
  return formatDurationMs(minutes * 60_000);
}

/** Milliseconds between now and a target instant (negative when in the past). */
export function msUntil(value: ISODateTime | Date | null | undefined): number | null {
  const date = toDate(value);
  return date ? differenceInMilliseconds(date, new Date()) : null;
}

/* ------------------------------------------------------------------ */
/* Numbers                                                             */
/* ------------------------------------------------------------------ */

const numberFormatter = new Intl.NumberFormat('it-IT');
const percentFormatter = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 });

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY;
  return numberFormatter.format(value);
}

/** Expects 0–100, not 0–1. */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return EMPTY;
  return `${percentFormatter.format(value)}%`;
}

const currencyFormatter = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return EMPTY;
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return EMPTY;
  return currencyFormatter.format(n);
}

/** Binary units, matching how backup vendors report volumes. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return EMPTY;
  if (bytes === 0) return '0 B';

  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];
  const exponent = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(1024)), units.length - 1);
  const scaled = bytes / 1024 ** exponent;
  const decimals = scaled >= 100 || exponent === 0 ? 0 : 1;
  return `${scaled.toFixed(decimals)} ${units[exponent]}`;
}

/* ------------------------------------------------------------------ */
/* Text                                                               */
/* ------------------------------------------------------------------ */

/** `in_lavorazione` → `In lavorazione` – for enum values without a dictionary. */
export function humanizeEnum(value: string | null | undefined): string {
  if (!value) return EMPTY;
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Initials for avatars, e.g. `Mario Rossi` → `MR`. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function truncate(value: string | null | undefined, max = 80): string {
  if (!value) return EMPTY;
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/** Calendar date in UTC, matching the rest of the API (`YYYY-MM-DD`). */
export function isoDateToday(): ISODate {
  return new Date().toISOString().slice(0, 10);
}

export function addIsoDays(iso: ISODate, days: number): ISODate {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function isoDayDiff(from: ISODate, to: ISODate): number {
  const start = Date.parse(`${from}T00:00:00.000Z`);
  const end = Date.parse(`${to}T00:00:00.000Z`);
  return Math.round((end - start) / 86_400_000);
}

export { EMPTY as EMPTY_VALUE };
